<template>
  <SlideBar @cambiar-estado="actualizarSidebar" />
  
  <div class="contenido" :class="{ 'contenido-expandido': sidebarCerrado }">
    <div class="pdf-container">

      <!-- Switcher de idioma — solo en pantalla, no en PDF -->
      <div class="lang-switcher-bar no-print">
        <button :class="{ active: locale === 'es' }" @click="setLang('es')">ES</button>
        <span class="lang-sep">|</span>
        <button :class="{ active: locale === 'en' }" @click="setLang('en')">EN</button>
        <span v-if="translating" class="translating-msg">Translating…</span>
      </div>

      <div v-if="consultaData" ref="pdfContent" class="pdf-content">
        <div class="encabezado">
          <div class="clinica-info">
            <h2>{{ $t('print.title') }}</h2>
            <p v-if="medicoInfo.nombre_completo"><strong>{{ $t('print.doctor') }}:</strong> {{ medicoInfo.nombre_completo }}</p>
            <p v-if="medicoInfo.cedula"><strong>{{ $t('print.license') }}:</strong> {{ medicoInfo.cedula }}</p>
          </div>
          <div class="fecha-folio">
            <img class="logo" src="@/assets/imagenes/logotipo.png" alt="Logo" />
            <p><strong>{{ $t('print.date') }}:</strong> {{ consultaData.fecha }}</p>
            <p><strong>{{ $t('print.folio') }}:</strong> {{ consultaData.folio }}</p>
          </div>
        </div>

        <table class="table">
          <thead>
            <tr><th colspan="2">{{ $t('print.patient') }}</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>{{ $t('print.full_name') }}:</strong></td>
              <td>{{ nombreCompleto }}</td>
            </tr>
            <tr>
              <td><strong>{{ $t('print.age') }}:</strong></td>
              <td>{{ consultaData.edad }} {{ $t('print.years') }}</td>
            </tr>
            <tr>
              <td><strong>{{ $t('print.gender') }}:</strong></td>
              <td>{{ nr('genero') }}</td>
            </tr>
            <tr>
              <td><strong>{{ $t('print.country') }}:</strong></td>
              <td>{{ nr('pais') }}</td>
            </tr>
            <tr>
              <td><strong>{{ $t('print.language') }}:</strong></td>
              <td>{{ nr('idioma') }}</td>
            </tr>
            <tr v-if="consultaData.nombre_hotel">
              <td><strong>{{ $t('print.hotel') }}:</strong></td>
              <td>{{ nr('nombre_hotel') }}</td>
            </tr>
            <tr v-if="consultaData.tipo_paciente">
              <td><strong>{{ $t('print.patient_type') }}:</strong></td>
              <td>{{ consultaData.tipo_paciente === 'colaborador' ? $t('print.collaborator') : $t('print.guest') }}</td>
            </tr>
          </tbody>
        </table>

        <div style="overflow-x: auto;">
          <table class="table" v-if="tieneVitales">
            <thead>
              <tr><th colspan="6">{{ $t('print.vitals') }}</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>{{ $t('print.bp') }}:</strong></td>
                <td>{{ nr('presion') }}</td>
                <td><strong>{{ $t('print.temp') }}:</strong></td>
                <td>{{ nr('temperatura', '°C') }}</td>
                <td><strong>{{ $t('print.hr') }}:</strong></td>
                <td>{{ nr('frecuencia', 'lpm') }}</td>
              </tr>
              <tr>
                <td><strong>{{ $t('print.spo2') }}:</strong></td>
                <td>{{ nr('saturacion', '%') }}</td>
                <td><strong>{{ $t('print.respiratory_rate') }}:</strong></td>
                <td colspan="2">{{ nr('frecuencia_respiratoria', 'rpm') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <table class="table" v-if="tieneAntropometricos">
          <thead>
            <tr><th colspan="4">{{ $t('print.anthropometrics') }}</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>{{ $t('print.weight') }}:</strong></td>
              <td>{{ nr('peso', 'kg') }}</td>
              <td><strong>{{ $t('print.height') }}:</strong></td>
              <td>{{ nr('talla', 'cm') }}</td>
            </tr>
            <tr>
              <td><strong>{{ $t('print.bmi') }}:</strong></td>
              <td>{{ imcCalculado || $t('print.not_registered') }}</td>
              <td><strong>{{ $t('print.nutrition_goal') }}:</strong></td>
              <td>{{ nr('objetivo_nutricional') }}</td>
            </tr>
          </tbody>
        </table>

        <table class="table" v-if="serviciosAdicionalesSeleccionados.length">
          <thead>
            <tr><th>{{ $t('print.additional_services') }}</th></tr>
          </thead>
          <tbody>
            <tr v-for="s in serviciosAdicionalesSeleccionados" :key="s.clave">
              <td>{{ s.concepto }}</td>
            </tr>
          </tbody>
        </table>

        <table class="table" v-if="tieneEvaluacionPsicologica">
          <thead>
            <tr><th>{{ $t('print.psych_eval') }}</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p><strong>{{ $t('print.consult_reason') }}:</strong> {{ nr('motivo_consulta') }}</p>
                <p><strong>{{ $t('print.mood') }}:</strong> {{ nr('estado_animo') }}</p>
                <p><strong>{{ $t('print.risk_level') }}:</strong> {{ nr('nivel_riesgo') }}</p>
              </td>
            </tr>
          </tbody>
        </table>

        <table class="table">
          <thead>
            <tr><th>{{ $t('print.history') }}</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p><strong>{{ $t('print.diseases') }}:</strong> {{ nr('enfermedades') }}</p>
                <p><strong>{{ $t('print.allergies') }}:</strong> {{ nr('alergias') }}</p>
                <p><strong>{{ $t('print.medication') }}:</strong> {{ nr('medicacion') }}</p>
              </td>
            </tr>
          </tbody>
        </table>

        <table class="table">
          <thead>
            <tr><th>{{ $t('print.diagnosis') }}</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <template v-if="consultaData.sintomas">
                  <p><strong>{{ $t('print.symptoms') }}:</strong></p>
                  <p>{{ nr('sintomas') }}</p>
                </template>
                <p><strong>{{ $t('print.diagnosis_label') }}:</strong></p>
                <p>{{ nr('diagnostico') }}</p>
              </td>
            </tr>
          </tbody>
        </table>

        <table class="table">
          <thead>
            <tr><th>{{ $t('print.observations') }}</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ nr('observaciones') }}</td>
            </tr>
          </tbody>
        </table>

        <!-- ═══ SECCIÓN MÉDICO (firma) ═══ -->
        <table class="table medico-footer">
          <tbody>
            <tr>
              <td style="border: none; padding-top: 20px;">
                <div style="display: flex; justify-content: flex-end; align-items: flex-end; flex-wrap: wrap;">
                  <div style="text-align: center;">
                    <div v-if="medicoInfo.firma_archivo" style="margin-bottom: 4px;">
                      <img :src="medicoInfo.firma_archivo" alt="Firma del médico" style="max-height: 50px; max-width: 180px; border-bottom: 1px solid #333;" />
                    </div>
                    <div v-else style="border-bottom: 1px solid #333; min-width: 120px; height: 30px;"></div>
                    <span style="font-size: 12px; color: #555;">{{ $t('print.signature') }}</span>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Dirección: informativa, discreta al pie del documento -->
        <p class="direccion-pie">{{ direccionReceta }}</p>

      </div>

      <div class="botonera no-print">
        <button class="action-button" @click="generarPDF"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;">download</span>{{ $t('print.download') }}</button>
        <button class="action-button" @click="enviarPorCorreo"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;">email</span>{{ $t('print.send') }}</button>
        <button class="action-button secondary" @click="volverARegistro"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;">arrow_back</span>{{ $t('print.back') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import SlideBar from '@/components/SlideBar.vue';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Swal from 'sweetalert2';
import { getConsultaByFolio, getDireccionReceta, SERVICIOS_ADICIONALES } from '@/utils/api';
import i18n from '@/i18n';

export default {
  name: 'ConsultaPreview',
  components: { SlideBar },
  data() {
    return {
      sidebarCerrado: false,
      consultaData: null,
      translatedData: null,
      translating: false,
      ocultarFirma: false,
      locale: localStorage.getItem('sanatia_lang') || 'es',
      // Almacenar información del médico
      medicoInfo: {},
      // Dirección dinámica de la receta
      direccionReceta: '',
    };
  },
  computed: {
    displayData() {
      if (this.locale === 'en' && this.translatedData) return this.translatedData;
      return this.consultaData;
    },
    nombreCompleto() {
      if (!this.consultaData) return '';
      return `${this.consultaData.nombre || ''} ${this.consultaData.apellido_paterno || ''} ${this.consultaData.apellido_materno || ''}`.trim();
    },
    tieneVitales() {
      const c = this.consultaData;
      return !!(c && (c.presion || c.frecuencia || c.temperatura || c.saturacion || c.frecuencia_respiratoria));
    },
    tieneAntropometricos() {
      const c = this.consultaData;
      return !!(c && (c.peso || c.talla || c.objetivo_nutricional));
    },
    tieneEvaluacionPsicologica() {
      const c = this.consultaData;
      return !!(c && (c.motivo_consulta || c.estado_animo || c.nivel_riesgo));
    },
    serviciosAdicionalesSeleccionados() {
      const claves = this.consultaData?.servicios_adicionales || [];
      return SERVICIOS_ADICIONALES.filter(s => claves.includes(s.clave));
    },
    imcCalculado() {
      const c = this.consultaData;
      const peso = Number(c?.peso);
      const tallaM = Number(c?.talla) / 100;
      if (!peso || !tallaM) return '';
      return (peso / (tallaM * tallaM)).toFixed(1);
    },
  },
  async created() {
    const folioParam = this.$route.params.folio;
    this.cargarDatosPorFolio(folioParam);
    this.direccionReceta = await getDireccionReceta();
  },
  methods: {
    actualizarSidebar(estado) {
      this.sidebarCerrado = estado;
    },
    setLang(lang) {
      this.locale = lang;
      localStorage.setItem('sanatia_lang', lang);
      i18n.global.locale.value = lang;
      if (lang === 'en' && !this.translatedData && this.consultaData) {
        this.translateConsulta();
      }
    },
    nr(campo, unidad = '') {
      const v = this.displayData?.[campo];
      if (!v && v !== 0) return this.$t('print.not_registered');
      return unidad ? `${v} ${unidad}` : String(v);
    },
    async translateText(text) {
      try {
        const res = await fetch(
          `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=es|en`
        );
        const data = await res.json();
        return data.responseData?.translatedText || text;
      } catch {
        return text;
      }
    },
    async translateConsulta() {
      if (!this.consultaData) return;
      this.translating = true;
      const fields = [
        'genero', 'pais', 'enfermedades', 'alergias', 'medicacion', 'sintomas', 'diagnostico', 'observaciones', 'idioma',
        'objetivo_nutricional', 'motivo_consulta', 'estado_animo', 'nivel_riesgo',
      ];
      const translated = { ...this.consultaData };
      for (const field of fields) {
        const val = this.consultaData[field];
        if (val) translated[field] = await this.translateText(val);
      }
      this.translatedData = translated;
      this.translating = false;
    },
    async cargarDatosPorFolio(folio) {
      const consulta = await getConsultaByFolio(folio);
      if (consulta) {
        this.consultaData = { ...consulta };
        this.medicoInfo = consulta.doctor_nombre_completo ? {
          nombre_completo: consulta.doctor_nombre_completo,
          cedula: consulta.doctor_cedula || '—',
          firma_archivo: consulta.doctor_firma_archivo || null,
        } : {};
        if (this.locale === 'en') {
          this.translateConsulta();
        }
      } else {
        Swal.fire('Error', 'No se encontró el folio', 'error');
        this.$router.push('/registro');
      }
    },
    async generarPDF() {
      const pdfContent = this.$refs.pdfContent;
      if (!pdfContent) return;

      const canvas = await html2canvas(pdfContent, {
        scale: 2,
        useCORS: true,
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'letter'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight > pageHeight - 20) {
        const scaleFactor = (pageHeight - 20) / imgHeight;
        const finalWidth = imgWidth * scaleFactor;
        const finalHeight = imgHeight * scaleFactor;
        pdf.addImage(imgData, 'PNG', (pageWidth - finalWidth) / 2, 10, finalWidth, finalHeight);
      } else {
        pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
      }

      pdf.save(`consulta_${this.consultaData.folio}.pdf`);
    },
    async enviarPorCorreo() {
      const pdfContent = this.$refs.pdfContent;
      if (!pdfContent) return;

      this.ocultarFirma = true;
      await this.$nextTick();

      const canvas = await html2canvas(pdfContent, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let position = 0;
      if (imgHeight > pageHeight) {
        let remainingHeight = imgHeight;
        while (remainingHeight > 0) {
          pdf.addImage(imgData, 'PNG', 0, position - (imgHeight - remainingHeight), imgWidth, imgHeight);
          remainingHeight -= pageHeight;
          if (remainingHeight > 0) pdf.addPage();
        }
      } else {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      }

      this.ocultarFirma = false;

      // Pedir correo
      const { value: destinatario } = await Swal.fire({
        title: 'Enviar por correo',
        input: 'email',
        inputLabel: 'Ingresa el correo electrónico del destinatario:',
        inputPlaceholder: 'ejemplo@correo.com',
        showCancelButton: true,
        confirmButtonText: 'Enviar',
        cancelButtonText: 'Cancelar',
        inputValidator: (value) => {
          if (!value) return 'El correo es obligatorio';
        }
      });

      if (!destinatario) return;

      Swal.fire({
        title: 'Enviando correo...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      Swal.fire({
        icon: 'info',
        title: 'Función no disponible',
        text: 'El envío de correo no está disponible en modo demo. Descarga el PDF y compártelo manualmente.',
        confirmButtonColor: '#133e68',
      });
    },
    volverARegistro() {
      this.$router.push('/registro');
    }
  }
};
</script>

<style scoped>
/* ============================================================
   DOMÉDICA - ESTILOS PARA SECCIÓN PDF (RECETAS / EXPEDIENTES)
   Basado en el Manual de Identidad (PDF)
   ============================================================ */

/* ---------- LANG SWITCHER ---------- */
.lang-switcher-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-bottom: 12px;
}

.lang-switcher-bar button {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #aaa;
  padding: 4px 2px;
  transition: color 0.15s;
}

.lang-switcher-bar button.active {
  color: var(--brand-primary);
}

.lang-switcher-bar button:hover {
  color: var(--brand-primary);
}

.lang-sep {
  color: #ccc;
  font-size: 12px;
  user-select: none;
}

.no-print { display: revert; }

.translating-msg {
  margin-left: 10px;
  font-size: 12px;
  color: var(--brand-primary);
  font-style: italic;
  animation: pulse 1s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
}

/* ---------- CONTENIDO PRINCIPAL ---------- */
.contenido {
  margin-left: 250px;
  padding: 20px;
  transition: margin-left 0.3s ease;
  background-color: var(--brand-beige);   /* #F4EFEB - Fondo de la página */
  min-height: 100vh;
}

.contenido-expandido {
  margin-left: 80px;                     /* Espacio para sidebar (si existe) */
}

/* ---------- CONTENEDOR DEL PDF ---------- */
.pdf-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

/* ---------- TARJETA DEL PDF (fondo blanco) ---------- */
.pdf-content {
  background-color: var(--white);         /* #FFFFFF */
  padding: 30px 35px;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(19, 62, 104, 0.12); /* Sombra con tono de marca */
  margin-bottom: 20px;
  transition: box-shadow 0.3s ease;
}

.pdf-content:hover {
  box-shadow: 0 10px 30px rgba(19, 62, 104, 0.18);
}

/* ---------- ENCABEZADO DEL PDF ---------- */
.encabezado {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--brand-secondary-light); /* #999999 */
}

/* --- Logo (ajustar según tu diseño) --- */
.logo {
  width: 80%;
  max-width: 180px;
  height: auto;
  margin-top: 10%;
  display: block;
}

/* --- Información de la clínica --- */
.clinica-info h2 {
  margin: 0 0 6px 0;
  color: var(--brand-primary);            /* #133E68 */
  font-weight: 700;
  font-size: 22px;
  letter-spacing: -0.3px;
}

.clinica-info p {
  margin: 4px 0;
  font-size: 14px;
  color: var(--brand-secondary);          /* #575756 */
  font-weight: 500;
}

/* --- Fecha y folio (alineados a la derecha) --- */
.fecha-folio {
  text-align: right;
  min-width: 120px;
}

.fecha-folio p {
  margin: 4px 0;
  font-size: 14px;
  color: var(--brand-secondary);
  font-weight: 500;
}

.fecha-folio .folio {
  font-weight: 700;
  color: var(--brand-primary);
}

/* --- Dirección al pie: discreta, tipografía pequeña y opacidad reducida --- */
.direccion-pie {
  margin: 16px 0 0;
  text-align: center;
  font-size: 11px;
  color: var(--brand-secondary);
  opacity: 0.55;
}

/* ---------- TABLA DE CONTENIDO ---------- */
.table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  font-size: 14px;
}

/* Cabecera de la tabla (60% Dominante - Azul Profundo) */
.table th {
  background-color: var(--brand-primary); /* #133E68 */
  color: var(--white);
  padding: 12px 15px;
  text-align: left;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

/* Celdas de la tabla */
.table td {
  padding: 12px 15px;
  border: 1px solid var(--brand-secondary-light); /* #999999 */
  color: var(--brand-secondary);
  font-weight: 500;
}

/* Filas alternas (opcional, para mejor legibilidad) */
.table tbody tr:nth-child(even) {
  background-color: #f9f8f7; /* Beige muy claro, sutil */
}

/* Hover en filas (mejora UX) */
.table tbody tr:hover {
  background-color: var(--brand-beige);
  transition: background-color 0.2s ease;
}

/* ---------- BOTONERA DE ACCIONES ---------- */
.botonera {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 20px;
}

/* Botón de acción (10% Acento - Verde Salvia) */
.action-button {
  padding: 12px 28px;
  background-color: var(--brand-accent-sage); /* #607F70 */
  color: var(--white);
  font-weight: 700;
  font-size: 16px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(96, 127, 112, 0.25);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.action-button:hover {
  background-color: var(--brand-accent-navy); /* #305065 */
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(48, 80, 101, 0.35);
}

.action-button:active {
  transform: scale(0.96);
}

/* Variante de botón secundario (opcional) */
.action-button.secondary {
  background-color: var(--brand-secondary-light);
  box-shadow: none;
}

.action-button.secondary:hover {
  background-color: var(--brand-secondary);
}

/* ---------- RESPONSIVE ---------- */
@media (max-width: 768px) {
  .contenido {
    margin-left: 0 !important;
    padding: 12px;
    padding-top: 70px;
  }

  .contenido-expandido {
    margin-left: 0 !important;
  }

  .pdf-container {
    padding: 10px;
  }

  .pdf-content {
    padding: 20px 16px;
  }

  .encabezado {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 15px;
  }

  .logo {
    width: 60%;
    margin-top: 5%;
  }

  .clinica-info h2 {
    font-size: 20px;
  }

  .fecha-folio {
    text-align: center;
  }

  .table th,
  .table td {
    padding: 8px 10px;
    font-size: 13px;
  }

  .botonera {
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .action-button {
    width: 100%;
    max-width: 280px;
    justify-content: center;
    font-size: 14px;
    padding: 12px 20px;
  }
}

@media (max-width: 480px) {
  .pdf-content {
    padding: 16px 12px;
  }

  .table th,
  .table td {
    font-size: 12px;
    padding: 6px 8px;
  }

  .clinica-info h2 {
    font-size: 18px;
  }

  .action-button {
    font-size: 13px;
    padding: 10px 16px;
  }
}

/* ============================================================
   ESTILOS PARA IMPRESIÓN (print)
   ============================================================ */

@media print {
  /* Ocultar elementos no deseados durante la impresión */
  .no-print,
  .botonera,
  .action-button {
    display: none !important;
  }

  .contenido {
    margin: 0 !important;
    padding: 0 !important;
    background-color: white !important;
  }

  .contenido-expandido {
    margin-left: 0 !important;
  }

  .pdf-container {
    max-width: 100%;
    padding: 0;
    margin: 0;
  }

  .pdf-content {
    width: 100%;
    max-width: 7.5in; /* Ancho menor que carta para márgenes */
    margin: 0 auto;
    padding: 0.3in;
    box-shadow: none !important;
    border-radius: 0;
    background: white;
  }

  .encabezado {
    border-bottom-color: #999999;
    margin-bottom: 15px;
    padding-bottom: 10px;
  }

  .clinica-info h2 {
    color: #133E68 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Asegurar que los colores se impriman */
  .table th {
    background-color: #133E68 !important;
    color: white !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .table td {
    border-color: #999999 !important;
    color: #575756 !important;
  }

  .table tbody tr:nth-child(even) {
    background-color: #f9f8f7 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .logo {
    max-width: 120px;
  }

  .fecha-folio .folio {
    color: #133E68 !important;
  }

  /* Página en blanco y márgenes */
  @page {
    margin: 0.5in;
    size: letter;
  }

  body {
    background: white;
    margin: 0;
    padding: 0;
  }
}
</style>
